/**
 * Serviço de Habilidades (Atividades Pedagógicas)
 *
 * Gerencia operações CRUD de atividades pedagógicas usando PostgREST.
 * Tabela: atividades_pedagogicas (id é TEXT, não UUID)
 */

import { postgrestClient } from "../lib/postgrest/client";

/**
 * Serviço de habilidades (atividades pedagógicas)
 */
export const habilidadesService = {
  /**
   * Lista atividades pedagógicas com filtros
   * @param {Object} options
   * @param {Object} options.filters - Filtros (ex: { categoria: "eq.Linguagens", obrigatoria: "eq.true" })
   * @param {string} options.order - Ordenação (ex: "nome.asc")
   * @param {number} options.limit - Limite de resultados
   * @param {number} options.offset - Offset para paginação
   * @param {string} options.search - Busca por nome
   * @param {boolean} options.includeCount - Se true, retorna { data, total }
   * @returns {Promise<Array|{data: Array, total: number}>}
   */
  async list(options = {}) {
    const { filters = {}, order = "nome.asc", limit, offset, search, includeCount = false } = options;
    
    // Se houver busca, adiciona filtro de nome
    if (search) {
      filters.nome = `ilike.*${search}*`;
    }
    
    const result = await postgrestClient.get("atividades_pedagogicas", {
      filters,
      order,
      limit,
      offset,
      includeCount,
    });
    
    // Se includeCount, retorna { data, total }, senão retorna array direto
    if (includeCount && result && typeof result === 'object' && 'data' in result) {
      return result;
    }
    return result;
  },

  /**
   * Busca atividade pedagógica por ID (TEXT)
   * @param {string} id
   * @returns {Promise<Object>}
   */
  async getById(id) {
    const result = await postgrestClient.get("atividades_pedagogicas", {
      filters: { id: `eq.${id}` },
    });
    return Array.isArray(result) ? result[0] : result;
  },

  /**
   * Cria nova atividade pedagógica
   * @param {Object} habilidadeData - Deve incluir id (TEXT), nome, categoria, obrigatoria
   * @returns {Promise<Object>}
   */
  async create(habilidadeData) {
    // Garante que obrigatoria seja booleano
    if (habilidadeData.obrigatoria !== undefined) {
      habilidadeData.obrigatoria = Boolean(habilidadeData.obrigatoria);
    }
    
    const result = await postgrestClient.post("atividades_pedagogicas", habilidadeData);
    return Array.isArray(result) ? result[0] : result;
  },

  /**
   * Atualiza atividade pedagógica
   * @param {string} id
   * @param {Object} habilidadeData
   * @returns {Promise<Object>}
   */
  async update(id, habilidadeData) {
    // Garante que obrigatoria seja booleano
    if (habilidadeData.obrigatoria !== undefined) {
      habilidadeData.obrigatoria = Boolean(habilidadeData.obrigatoria);
    }
    
    const result = await postgrestClient.patch("atividades_pedagogicas", { id }, habilidadeData);
    return Array.isArray(result) ? result[0] : result;
  },

  /**
   * Remove atividade pedagógica
   * @param {string} id
   * @returns {Promise<null>}
   */
  async delete(id) {
    return postgrestClient.delete("atividades_pedagogicas", { id });
  },

  /**
   * Busca atividades pedagógicas por categoria
   * @param {string} categoria
   * @returns {Promise<Array>}
   */
  async getByCategoria(categoria) {
    return this.list({
      filters: { categoria: `eq.${categoria}` },
    });
  },

  /**
   * Busca atividades pedagógicas obrigatórias
   * @returns {Promise<Array>}
   */
  async getObrigatorias() {
    return this.list({
      filters: { obrigatoria: "eq.true" },
    });
  },
};

export default habilidadesService;

